import { z } from 'zod';

import type { BlockReason } from './block_reason';
import type { TaskStatus } from './task';

/**
 * Presentation-level task status. Flattens the persisted
 * `(status, blockReason)` pair into the six states the UI renders as a
 * single glyph/label — promoting the two block reasons users act on
 * (human approval, awaiting the client) to first-class states and
 * dropping the `driverKind` colour split on `running`.
 *
 *  - `waiting_approval`  ← `blocked` + `human_in_the_loop`
 *  - `waiting_client`    ← `blocked` + `awaiting_input` from the client
 *  - `waiting_operation` ← `blocked` for any other reason (awaiting a
 *    vendor, scheduled) — a pending operation the agent is
 *    waiting on, rendered with a clock glyph.
 */
export const TaskDisplayStatusSchema = z.enum([
  'pending',
  'running',
  'waiting_client',
  'waiting_operation',
  'waiting_approval',
  'failed',
  'done',
]);

export type TaskDisplayStatus = z.infer<typeof TaskDisplayStatusSchema>;

/**
 * Derives the {@link TaskDisplayStatus} from a task's persisted
 * `(status, blockReason)`. Pure — shared by the web UI and the Kanban
 * API so both agree on the mapping.
 */
export function deriveTaskDisplayStatus(task: {
  status: TaskStatus;
  blockReason: BlockReason | null;
}): TaskDisplayStatus {
  switch (task.status) {
    case 'pending':
      return 'pending';
    case 'running':
      return 'running';
    case 'done':
      return 'done';
    case 'failed':
      return 'failed';
    case 'blocked':
      switch (task.blockReason?.kind) {
        case 'human_in_the_loop':
          return 'waiting_approval';
        case 'awaiting_input':
          return task.blockReason.source === 'client' ? 'waiting_client' : 'waiting_operation';
        case 'scheduled':
        case undefined:
          return 'waiting_operation';
        default: {
          const exhaustive: never = task.blockReason;
          return exhaustive;
        }
      }
    default: {
      const exhaustive: never = task.status;
      return exhaustive;
    }
  }
}
